import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient, useAgentContext, useMyProfile } from '@dogdisk/shared'
import { utc8DayString } from './utc8-day'

/** 无归属代理 / 取不到域名时的固定验证码内容（owner 决策 2026-06-30）。 */
export const DOMAIN_CAPTCHA_FALLBACK = '绅士网盘'
/** 兜底内容的固定分词（本地测试环境无 agent_jump_chain 也走这里；owner 决策 2026-07-09）。 */
export const DOMAIN_CAPTCHA_FALLBACK_SEGMENTS = ['绅士', '网盘']

/** 普通用户验证码触发间隔：1 分钟一次（owner 决策 2026-07-09；原为 2 分钟）。 */
export const REGULAR_GATE_INTERVAL_MS = 60_000
/** 普通用户上次通过时间戳（epoch ms）localStorage 键。 */
const REGULAR_PASS_TS_KEY = 'domcap:reg:pass:ts'

function readLS(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}
function writeLS(key: string, val: string): void {
  try {
    localStorage.setItem(key, val)
  } catch {
    // ignore (隐私模式 / SSR)
  }
}

/** 端点单条：域名 + 顺序点选片段（后端拼音分词，spec 2026-07-09 §3）。 */
type JumpDomainEntry = { domain: string; segments: string[] }

export type DomainCaptchaGate = {
  /** 验证码内容：域名 或 DOMAIN_CAPTCHA_FALLBACK */
  content: string
  /** 顺序点选片段（后端拼音分词；兜底时为 DOMAIN_CAPTCHA_FALLBACK_SEGMENTS）。 */
  segments: string[]
  /** 是否需要弹 gate（订阅用户当天未过 → true；普通用户距上次通过满 1 分钟 → true）。 */
  shouldGate: boolean
  /** 内容已就绪（域名查询结束 / 无归属直接就绪），article-detail 据此避免闪烁。 */
  ready: boolean
  /** 通过后调用：订阅用户写当天日键；普通用户写通过时间戳（1 分钟窗口）。 */
  markPassed: () => void
}

/**
 * useDomainCaptchaGate — 域名验证码 gate 决策。
 *
 * 归属优先级 cookie `_dd_agent` > profiles.first_agent_id（'default'/空视为无）。
 * 取该 agent active chain 的 L1/L2 域名随机一条（含后端分词 segments）；
 * 取不到用 DOMAIN_CAPTCHA_FALLBACK + 固定两段。
 * isSubscriber=true（有任意 VIP/创作者订阅段）每天一次（UTC+8 日键），
 * 否则普通用户每 1 分钟一次（localStorage 时间戳窗口）。
 */
export function useDomainCaptchaGate({ isSubscriber }: { isSubscriber: boolean }): DomainCaptchaGate {
  const agentCtx = useAgentContext()
  const cookieAgent = agentCtx.agent_id
  const profileQ = useMyProfile({ enabled: !cookieAgent })
  const profileAgent = profileQ.data?.agent_id

  const rawAgent = cookieAgent || profileAgent || ''
  const agentId = rawAgent && rawAgent !== 'default' ? rawAgent : ''

  const domainsQ = useQuery({
    queryKey: ['domcap', 'domains', agentId] as const,
    enabled: agentId !== '',
    staleTime: 5 * 60_000,
    queryFn: () =>
      apiClient.get<{ domains: JumpDomainEntry[] }>(
        `/gapi/public/agent/jump-domains?agent_id=${encodeURIComponent(agentId)}`,
      ),
  })

  // segments 为空的 entry 视为不可用（后端异常防御）→ 过滤后为空整体走兜底。
  const entries = (domainsQ.data?.domains ?? []).filter((e) => (e.segments?.length ?? 0) > 0)
  const picked = useMemo(
    () => (entries.length > 0 ? entries[Math.floor(Math.random() * entries.length)]! : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries.map((e) => e.domain).join('|')],
  )
  const content = picked?.domain ?? DOMAIN_CAPTCHA_FALLBACK
  const segments = picked?.segments ?? DOMAIN_CAPTCHA_FALLBACK_SEGMENTS

  // 无归属 → 立即就绪用兜底；有归属 → 等查询结束（成功或失败）。
  const ready = agentId === '' ? true : !domainsQ.isLoading

  const dayKey = `domcap:vip:pass:${utc8DayString()}`
  // 用 state 触发 markPassed 后的 re-render（localStorage 本身不触发）。
  const [tick, setTick] = useState(0)
  const shouldGate = useMemo(() => {
    void tick // 依赖 tick 以在 markPassed 后重算
    if (isSubscriber) return readLS(dayKey) !== '1'
    // 普通用户：距上次通过满 1 分钟才再弹。
    const raw = readLS(REGULAR_PASS_TS_KEY)
    const last = raw ? Number(raw) : NaN
    if (!Number.isFinite(last)) return true
    return Date.now() - last >= REGULAR_GATE_INTERVAL_MS
  }, [isSubscriber, dayKey, tick])

  const markPassed = () => {
    if (isSubscriber) writeLS(dayKey, '1')
    else writeLS(REGULAR_PASS_TS_KEY, String(Date.now()))
    setTick((t) => t + 1)
  }

  return { content, segments, shouldGate, ready, markPassed }
}
